import { CHECK_STATUS } from '../types';
import domainWhitelist from '../domainWhitelist.json';
import abortAfter from './abortAfter';

export const id = 'portDomainReachability';
export const label = 'Port / Domain Reachability';

const TIMEOUT_MS = 5000;
const MAX_RESULTS_SHOWN = 20;

export async function run(ctx) {
  const start = Date.now();
  const deviceType = (ctx.deviceType || '').toLowerCase();

  // Device-scoped filtering
  const scopedEntries = domainWhitelist.filter((entry) => {
    if (entry.devices === 'All') return true;
    if (entry.devices === 'BrightSign' && deviceType === 'brightsign') return true;
    if (
      entry.devices === 'Skykit' &&
      (deviceType === 'skykit' || deviceType === 'androidskykit')
    )
      return true;
    return false;
  });

  const deviceScopedOut = domainWhitelist.length - scopedEntries.length;

  const wildcardEntries = scopedEntries.filter((e) =>
    e.domain.startsWith('*')
  );
  // Non-443 entries (NTP:123, HTTP:80) cannot be probed via browser fetch:
  // NTP has no HTTP layer; HTTP:80 is blocked by mixed-content from HTTPS origin.
  // Exclude to prevent systematic false AT_RISK on Skykit / BrightSign.
  const nonHttpsSkipped = scopedEntries.filter(
    (e) => !e.domain.startsWith('*') && (e.port || 443) !== 443
  ).length;
  const probableEntries = scopedEntries.filter(
    (e) => !e.domain.startsWith('*') && (e.port || 443) === 443
  );

  // Run probes in parallel. Each probe resolves (never rejects): a resolved
  // fetch means reachable, a rejected one means unreachable.
  const results = await Promise.all(
    probableEntries.map(async (entry) => {
      let reachable = false;
      try {
        await fetch(`https://${entry.domain}${entry.path || ''}`, {
          mode: 'no-cors',
          signal: abortAfter(TIMEOUT_MS),
        });
        reachable = true;
      } catch (_err) {
        reachable = false;
      }
      return { domain: entry.domain, port: entry.port || 443, reachable, critical: entry.critical === true };
    })
  );

  const criticalUnreachable = results
    .filter((r) => !r.reachable && r.critical)
    .map((r) => `${r.domain}:${r.port}`);
  const nonCriticalUnreachable = results
    .filter((r) => !r.reachable && !r.critical)
    .map((r) => `${r.domain}:${r.port}`);
  const unresolvedCount = criticalUnreachable.length + nonCriticalUnreachable.length;
  const totalCount = results.length;
  const wildcardSkipped = wildcardEntries.length;

  const durationMs = Date.now() - start;

  if (unresolvedCount === 0) {
    return {
      id,
      label,
      status: CHECK_STATUS.PASS,
      detail: `All ${totalCount} domains reachable`,
      detailData: { totalCount, unresolvedCount: 0, wildcardSkipped, nonHttpsSkipped, deviceScopedOut, deviceType },
      durationMs,
    };
  }

  if (criticalUnreachable.length > 0) {
    return {
      id,
      label,
      status: CHECK_STATUS.FAIL,
      detail: `${criticalUnreachable.length} critical domain(s) unreachable`,
      detailData: {
        totalCount,
        unresolvedCount,
        wildcardSkipped,
        nonHttpsSkipped,
        deviceScopedOut,
        deviceType,
        criticalUnreachable: criticalUnreachable.slice(0, MAX_RESULTS_SHOWN),
        nonCriticalUnreachable: nonCriticalUnreachable.slice(0, MAX_RESULTS_SHOWN),
      },
      durationMs,
    };
  }

  return {
    id,
    label,
    status: CHECK_STATUS.AT_RISK,
    detail: `${nonCriticalUnreachable.length} non-critical domain(s) unreachable`,
    detailData: {
      totalCount,
      unresolvedCount,
      wildcardSkipped,
      nonHttpsSkipped,
      deviceScopedOut,
      deviceType,
      nonCriticalUnreachable: nonCriticalUnreachable.slice(0, MAX_RESULTS_SHOWN),
    },
    durationMs,
  };
}
